/**
 * =============================================================================
 * SPEQ55 RFP Pro - User Management Admin Page
 * =============================================================================
 *
 * Admin page for managing users.
 * - View all users with filtering
 * - Update user roles and status
 * - Deactivate/reactivate users
 * - Delete users (super_admin only)
 *
 * @module pages/admin/UserManagement
 * @author SPEQ55
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  getAllUsers,
  createUser,
  updateUser,
  deactivateUser,
  reactivateUser,
  deleteUser,
  resetUserPassword,
} from '../../api/users';
import { useAuth } from '../../contexts/AuthContext';
import { getOrganizations } from '../../api/organizations';

function UserManagement() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');

  // Modal state
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState(null);

  // Form state
  const [editRole, setEditRole] = useState('');
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Create user form state
  const [createEmail, setCreateEmail] = useState('');
  const [createUsername, setCreateUsername] = useState('');
  const [createPassword, setCreatePassword] = useState('');
  const [createConfirmPassword, setCreateConfirmPassword] = useState('');
  const [createFirstName, setCreateFirstName] = useState('');
  const [createLastName, setCreateLastName] = useState('');
  const [createRole, setCreateRole] = useState('end_user');
  const [createOrganizationId, setCreateOrganizationId] = useState('');

  // Organization data for dropdowns
  const [organizations, setOrganizations] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [editOrganizationId, setEditOrganizationId] = useState('');

  // Check permissions
  const isSuperAdmin = user?.role === 'super_admin';
  const canManageUsers = user?.role === 'super_admin' || user?.role === 'admin_sales';

  const isTenantRole = (role) => role === 'org_admin' || role === 'end_user';

  const fetchOrganizations = useCallback(async () => {
    if (organizations.length > 0) return;
    setOrgsLoading(true);
    try {
      const data = await getOrganizations({ is_active: true, limit: 100 });
      setOrganizations(data.organizations || []);
    } catch (err) {
      console.error('Failed to load organizations', err);
    } finally {
      setOrgsLoading(false);
    }
  }, [organizations.length]);

  const orgNameMap = useMemo(() => {
    const map = {};
    organizations.forEach((org) => {
      map[org.id] = org.name;
    });
    return map;
  }, [organizations]);

  /**
   * Load users with current filters.
   */
  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const filters = {};

      if (statusFilter === 'active') {
        filters.activeOnly = true;
      } else if (statusFilter === 'inactive') {
        filters.activeOnly = false;
      }

      if (roleFilter !== 'all') {
        filters.role = roleFilter;
      }

      const data = await getAllUsers(filters);
      setUsers(data.users || []);
    } catch {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, roleFilter]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  /**
   * Filter users by search term.
   */
  const filteredUsers = users.filter((u) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      u.email?.toLowerCase().includes(search) ||
      u.username?.toLowerCase().includes(search) ||
      u.first_name?.toLowerCase().includes(search) ||
      u.last_name?.toLowerCase().includes(search)
    );
  });

  /**
   * Open edit modal for a user.
   */
  const openEditModal = (userToEdit) => {
    setSelectedUser(userToEdit);
    setEditRole(userToEdit.role);
    setEditFirstName(userToEdit.first_name || '');
    setEditLastName(userToEdit.last_name || '');
    setEditOrganizationId(userToEdit.organization_id || '');
    setModalError(null);
    setShowEditModal(true);
    fetchOrganizations();
  };

  /**
   * Close edit modal.
   */
  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedUser(null);
    setEditOrganizationId('');
    setModalError(null);
  };

  /**
   * Open delete confirmation modal.
   */
  const openDeleteModal = (userToDelete) => {
    setSelectedUser(userToDelete);
    setModalError(null);
    setShowDeleteModal(true);
  };

  /**
   * Close delete modal.
   */
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedUser(null);
    setModalError(null);
  };

  /**
   * Open password reset modal.
   */
  const openPasswordModal = (userToReset) => {
    setSelectedUser(userToReset);
    setNewPassword('');
    setConfirmPassword('');
    setModalError(null);
    setShowPasswordModal(true);
  };

  /**
   * Close password reset modal.
   */
  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setSelectedUser(null);
    setNewPassword('');
    setConfirmPassword('');
    setModalError(null);
  };

  /**
   * Open create user modal.
   */
  const openCreateModal = () => {
    setCreateEmail('');
    setCreateUsername('');
    setCreatePassword('');
    setCreateConfirmPassword('');
    setCreateFirstName('');
    setCreateLastName('');
    setCreateRole('end_user');
    setCreateOrganizationId('');
    setModalError(null);
    setShowCreateModal(true);
    fetchOrganizations();
  };

  /**
   * Close create user modal.
   */
  const closeCreateModal = () => {
    setShowCreateModal(false);
    setModalError(null);
  };

  /**
   * Handle user update.
   */
  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    setModalError(null);

    try {
      const updates = {};

      if (editRole !== selectedUser.role) {
        updates.role = editRole;
      }

      if (editFirstName !== selectedUser.first_name) {
        updates.firstName = editFirstName;
      }

      if (editLastName !== selectedUser.last_name) {
        updates.lastName = editLastName;
      }

      if (editOrganizationId !== (selectedUser.organization_id || '')) {
        updates.organizationId = editOrganizationId || null;
      }

      if (Object.keys(updates).length > 0) {
        await updateUser(selectedUser.id, updates);
        await loadUsers();
        closeEditModal();
      } else {
        closeEditModal();
      }
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to update user');
    } finally {
      setModalLoading(false);
    }
  };

  /**
   * Handle user deactivation.
   */
  const handleDeactivate = async (userId) => {
    try {
      await deactivateUser(userId);
      await loadUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to deactivate user');
    }
  };

  /**
   * Handle user reactivation.
   */
  const handleReactivate = async (userId) => {
    try {
      await reactivateUser(userId);
      await loadUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to reactivate user');
    }
  };

  /**
   * Handle user deletion.
   */
  const handleDelete = async () => {
    setModalLoading(true);
    setModalError(null);

    try {
      await deleteUser(selectedUser.id);
      await loadUsers();
      closeDeleteModal();
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to delete user');
    } finally {
      setModalLoading(false);
    }
  };

  /**
   * Handle password reset.
   */
  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    setModalError(null);

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setModalError('Passwords do not match');
      setModalLoading(false);
      return;
    }

    // Validate password length
    if (newPassword.length < 8) {
      setModalError('Password must be at least 8 characters');
      setModalLoading(false);
      return;
    }

    try {
      await resetUserPassword(selectedUser.id, newPassword);
      alert(`Password reset successfully for ${selectedUser.first_name} ${selectedUser.last_name}`);
      closePasswordModal();
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to reset password');
    } finally {
      setModalLoading(false);
    }
  };

  /**
   * Handle create user.
   */
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    setModalError(null);

    // Validate required fields
    if (!createEmail || !createUsername || !createPassword || !createFirstName || !createLastName) {
      setModalError('All fields except organization are required');
      setModalLoading(false);
      return;
    }

    // Validate organization is selected for tenant roles
    if (isTenantRole(createRole) && !createOrganizationId) {
      setModalError('Organization is required for Org Admin and End User roles');
      setModalLoading(false);
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(createEmail)) {
      setModalError('Invalid email format');
      setModalLoading(false);
      return;
    }

    // Validate username length
    if (createUsername.length < 3 || createUsername.length > 100) {
      setModalError('Username must be between 3 and 100 characters');
      setModalLoading(false);
      return;
    }

    // Validate passwords match
    if (createPassword !== createConfirmPassword) {
      setModalError('Passwords do not match');
      setModalLoading(false);
      return;
    }

    // Validate password length
    if (createPassword.length < 8) {
      setModalError('Password must be at least 8 characters');
      setModalLoading(false);
      return;
    }

    try {
      const userData = {
        email: createEmail,
        username: createUsername,
        password: createPassword,
        firstName: createFirstName,
        lastName: createLastName,
        role: createRole,
      };

      if (createOrganizationId) {
        userData.organizationId = createOrganizationId;
      }

      await createUser(userData);
      alert(`User created successfully: ${createEmail}`);
      await loadUsers();
      closeCreateModal();
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to create user');
    } finally {
      setModalLoading(false);
    }
  };

  /**
   * Get role badge color.
   */
  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'super_admin':
        return 'bg-purple-100 text-purple-800';
      case 'admin_sales':
        return 'bg-blue-100 text-blue-800';
      case 'org_admin':
        return 'bg-green-100 text-green-800';
      case 'end_user':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  /**
   * Format role name for display.
   */
  const formatRole = (role) => {
    const roleMap = {
      super_admin: 'Super Admin',
      admin_sales: 'Admin/Sales',
      org_admin: 'Org Admin',
      end_user: 'End User',
    };
    return roleMap[role] || role;
  };

  if (!canManageUsers) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">You do not have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-600 mt-1">Manage user accounts and permissions</p>
        </div>
        <button
          onClick={openCreateModal}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
        >
          <span className="text-xl">+</span>
          Create User
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div>
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <input
              id="search"
              type="text"
              placeholder="Search by name, email, username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Status Filter */}
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              id="status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Users</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>

          {/* Role Filter */}
          <div>
            <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
              Role
            </label>
            <select
              id="role"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Roles</option>
              <option value="super_admin">Super Admin</option>
              <option value="admin_sales">Admin/Sales</option>
              <option value="org_admin">Org Admin</option>
              <option value="end_user">End User</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            <p className="mt-2 text-gray-600">Loading users...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No users found matching your filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Organization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((u) => (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-blue-600 font-medium">
                            {u.first_name?.[0]}
                            {u.last_name?.[0]}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {u.first_name} {u.last_name}
                          </div>
                          <div className="text-sm text-gray-500">@{u.username}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{u.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadgeClass(u.role)}`}
                      >
                        {formatRole(u.role)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {orgNameMap[u.organization_id] || (u.organization_id ? '...' : '-')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {u.is_active ? (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Active
                        </span>
                      ) : (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(u.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => openEditModal(u)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit user"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => openPasswordModal(u)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Reset password"
                          disabled={u.id === user?.id}
                        >
                          Reset Password
                        </button>
                        {u.is_active ? (
                          <button
                            onClick={() => handleDeactivate(u.id)}
                            className="text-orange-600 hover:text-orange-900"
                            title="Deactivate user"
                            disabled={u.id === user?.id}
                          >
                            Deactivate
                          </button>
                        ) : (
                          <button
                            onClick={() => handleReactivate(u.id)}
                            className="text-green-600 hover:text-green-900"
                            title="Reactivate user"
                          >
                            Reactivate
                          </button>
                        )}
                        {isSuperAdmin && (
                          <button
                            onClick={() => openDeleteModal(u)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete user permanently"
                            disabled={u.id === user?.id}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
                Edit User: {selectedUser.first_name} {selectedUser.last_name}
              </h3>

              {modalError && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded p-3">
                  <p className="text-sm text-red-800">{modalError}</p>
                </div>
              )}

              <form onSubmit={handleUpdateUser}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                  <input
                    type="text"
                    value={editFirstName}
                    onChange={(e) => setEditFirstName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                  <input
                    type="text"
                    value={editLastName}
                    onChange={(e) => setEditLastName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select
                    value={editRole}
                    onChange={(e) => {
                      const newRole = e.target.value;
                      setEditRole(newRole);
                      if (!isTenantRole(newRole)) {
                        setEditOrganizationId('');
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {isSuperAdmin && <option value="super_admin">Super Admin</option>}
                    {isSuperAdmin && <option value="admin_sales">Admin/Sales</option>}
                    <option value="org_admin">Org Admin</option>
                    <option value="end_user">End User</option>
                  </select>
                </div>

                {isTenantRole(editRole) && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Organization
                    </label>
                    <select
                      value={editOrganizationId}
                      onChange={(e) => setEditOrganizationId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={orgsLoading}
                    >
                      <option value="">
                        {orgsLoading ? 'Loading organizations...' : '-- Select Organization --'}
                      </option>
                      {organizations
                        .filter((org) => org.name !== '_system')
                        .map((org) => (
                          <option key={org.id} value={org.id}>
                            {org.name}
                          </option>
                        ))}
                    </select>
                  </div>
                )}

                <div className="flex gap-3 justify-end">
                  <button
                    type="button"
                    onClick={closeEditModal}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                    disabled={modalLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    disabled={modalLoading}
                  >
                    {modalLoading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-[500px] shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">Create New User</h3>

              {modalError && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded p-3">
                  <p className="text-sm text-red-800">{modalError}</p>
                </div>
              )}

              <form onSubmit={handleCreateUser}>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input
                      type="email"
                      value={createEmail}
                      onChange={(e) => setCreateEmail(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      placeholder="user@example.com"
                      autoFocus
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Username *
                    </label>
                    <input
                      type="text"
                      value={createUsername}
                      onChange={(e) => setCreateUsername(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      minLength={3}
                      maxLength={100}
                      placeholder="username"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password *
                    </label>
                    <input
                      type="password"
                      value={createPassword}
                      onChange={(e) => setCreatePassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      minLength={8}
                      placeholder="Min 8 characters"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm Password *
                    </label>
                    <input
                      type="password"
                      value={createConfirmPassword}
                      onChange={(e) => setCreateConfirmPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      minLength={8}
                      placeholder="Re-enter password"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name *
                    </label>
                    <input
                      type="text"
                      value={createFirstName}
                      onChange={(e) => setCreateFirstName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      placeholder="John"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      value={createLastName}
                      onChange={(e) => setCreateLastName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      placeholder="Doe"
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                    <select
                      value={createRole}
                      onChange={(e) => {
                        const newRole = e.target.value;
                        setCreateRole(newRole);
                        if (!isTenantRole(newRole)) {
                          setCreateOrganizationId('');
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {isSuperAdmin && <option value="super_admin">Super Admin</option>}
                      {isSuperAdmin && <option value="admin_sales">Admin/Sales</option>}
                      <option value="org_admin">Org Admin</option>
                      <option value="end_user">End User</option>
                    </select>
                  </div>

                  {isTenantRole(createRole) && (
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Organization *
                      </label>
                      <select
                        value={createOrganizationId}
                        onChange={(e) => setCreateOrganizationId(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                        disabled={orgsLoading}
                      >
                        <option value="">
                          {orgsLoading ? 'Loading organizations...' : '-- Select Organization --'}
                        </option>
                        {organizations
                          .filter((org) => org.name !== '_system')
                          .map((org) => (
                            <option key={org.id} value={org.id}>
                              {org.name}
                            </option>
                          ))}
                      </select>
                    </div>
                  )}
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
                  <p className="text-sm text-blue-800">
                    ℹ️ The user will receive a welcome email with their login credentials.
                  </p>
                </div>

                <div className="flex gap-3 justify-end">
                  <button
                    type="button"
                    onClick={closeCreateModal}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                    disabled={modalLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    disabled={modalLoading}
                  >
                    {modalLoading ? 'Creating...' : 'Create User'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {showPasswordModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
                Reset Password: {selectedUser.first_name} {selectedUser.last_name}
              </h3>

              {modalError && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded p-3">
                  <p className="text-sm text-red-800">{modalError}</p>
                </div>
              )}

              <form onSubmit={handlePasswordReset}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New Password
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    minLength={8}
                    placeholder="Minimum 8 characters"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    minLength={8}
                    placeholder="Re-enter password"
                  />
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4">
                  <p className="text-sm text-yellow-800">
                    ⚠️ The user will need to use this new password to login. Make sure to
                    communicate it securely.
                  </p>
                </div>

                <div className="flex gap-3 justify-end">
                  <button
                    type="button"
                    onClick={closePasswordModal}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                    disabled={modalLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                    disabled={modalLoading}
                  >
                    {modalLoading ? 'Resetting...' : 'Reset Password'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">Delete User</h3>

              {modalError && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded p-3">
                  <p className="text-sm text-red-800">{modalError}</p>
                </div>
              )}

              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to permanently delete{' '}
                <strong>
                  {selectedUser.first_name} {selectedUser.last_name}
                </strong>
                ? This action cannot be undone.
              </p>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={closeDeleteModal}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                  disabled={modalLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                  disabled={modalLoading}
                >
                  {modalLoading ? 'Deleting...' : 'Delete User'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;
